import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import useCurrentState from "@/routes/useCurrentState";
import { getStateByCode, hasFeature, isTrackActive, trackStatus, statePath } from "@/lib/stateRegistry";
import { PUBLIC_LAUNCH_STATE_CODE, SHOW_BUSINESS_TRACK } from "@/lib/displayFlags";
import BrandMark from "@/components/BrandMark";

const nationalInsights = [
  { to: "/market-pulse", label: "Market pulse", d: "Energy market briefing" },
  { to: "/data-centers", label: "Data & the grid", d: "How AI is rewiring your bill" },
  { to: "/how-it-works", label: "How it works", d: "How RateCurrent works" },
];

/** Nav is built from the registry for the current state, so a state never links to another state's tools. */
export function buildNavForTest(state) { return buildNav(state); }

/**
 * 2026-09-30 reduced-scope early launch (Owner-approved, Witherspoon-CLO compliance/
 * 01_texas_disclosures.md Section 17; Reba-CPO product scope): ESID lookup and Bill-Check are
 * the site's entire live value prop now, so they're promoted to primary top-level nav. Plans and
 * Providers are merged into one deprioritized "Plans" item in the Insights dropdown, pointing at
 * the same registry-resolved coming-soon/waitlist flow real direct navigation already gets --
 * no new page. Solar is treated identically (coming-soon): RC has zero signed solar-buyback
 * partnerships, so it also moves to the dropdown and resolves to the same waitlist page via
 * stateRegistry's ROUTE_TRACK (Solar depends on the residential track, same as Plans/Providers).
 * Utilities (tdu_content) is untouched -- a real, live feature, still primary nav.
 */
function buildNav(state) {
  // A page with no state segment in the URL (home, national pages) used to get an empty primary
  // nav -- Plans/Providers/Utilities only appeared once you were already on a state page. With
  // SHOW_OTHER_STATES off, TX is the only live state, so there is no multi-state leak (the risk
  // the original null-state guard existed for) in falling back to the launch state's own nav here.
  const navState = state || getStateByCode(PUBLIC_LAUNCH_STATE_CODE);

  const primary = [];
  // Bill-Check is a national route (BillCheck.jsx has no state scope, no feature flag) -- always
  // primary regardless of which state's nav is being built.
  primary.push({ to: "/bill-check", label: "Check your bill" });
  if (navState) {
    if (hasFeature(navState, "esid_lookup")) primary.push({ to: statePath(navState.slug, "esid-lookup"), label: "Find my ESID" });
    if (SHOW_BUSINESS_TRACK && isTrackActive(navState, "commercial")) primary.push({ to: statePath(navState.slug, "commercial"), label: "For business" });
    if (hasFeature(navState, "tdu_content")) primary.push({ to: statePath(navState.slug, "utilities"), label: "Utilities" });
  }

  const insights = [...nationalInsights];
  if (navState && trackStatus(navState, "residential") !== "disabled") {
    insights.push({ to: statePath(navState.slug, "plans"), label: "Plan comparison", d: "Coming soon" });
  }
  if (navState && hasFeature(navState, "solar_content")) {
    insights.push({ to: statePath(navState.slug, "solar"), label: "Solar", d: `Solar buyback in ${navState.name} (coming soon)` });
  }

  let cta = { to: "/", label: "RateCurrent" };
  if (navState) {
    cta = isTrackActive(navState, "residential")
      ? { to: statePath(navState.slug, "plans"), label: state ? "Find plans" : `Find ${navState.name} plans` }
      : { to: statePath(navState.slug), label: `${navState.name} updates` };
  }
  return { primary, insights, cta };
}

export default function Header() {
  const state = useCurrentState();
  const { primary, insights, cta } = buildNav(state);
  const [open, setOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const insightsRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (insightsRef.current && !insightsRef.current.contains(e.target)) setInsightsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-100" data-testid="site-header">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="logo-link">
          <BrandMark className="text-xl" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {primary.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#0A2540]" : "text-slate-600 hover:text-[#0A2540]"}`}
            >
              {l.label}
            </NavLink>
          ))}

          <div className="relative" ref={insightsRef}>
            <button
              onClick={() => setInsightsOpen(!insightsOpen)}
              aria-expanded={insightsOpen}
              aria-haspopup="true"
              aria-controls="insights-dropdown"
              className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-[#0A2540] transition-colors"
              data-testid="nav-insights-toggle"
            >
              Insights <ChevronDown className={`w-4 h-4 transition-transform ${insightsOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {insightsOpen && (
              <div id="insights-dropdown" className="absolute top-full right-0 mt-3 w-80 bg-white border border-slate-100 rounded-2xl shadow-xl p-2" data-testid="insights-dropdown">
                {insights.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setInsightsOpen(false)}
                    className="flex flex-col gap-0.5 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                    data-testid={`nav-dropdown-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <span className="font-semibold text-sm text-[#0A2540]">{l.label}</span>
                    <span className="text-xs text-slate-500">{l.d}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link to={cta.to} className="btn-green !py-2 !px-5 !text-sm" data-testid="header-cta">{cta.label}</Link>
        </div>

        <button
          className="lg:hidden text-[#0A2540]"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>

      {open && (
        <nav id="mobile-menu" className="lg:hidden border-t border-slate-100 bg-white" aria-label="Mobile" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {[...primary, ...insights].map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-base font-medium py-2.5 border-b border-slate-100 text-[#0A2540]" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                {l.label}
              </Link>
            ))}
            <Link to={cta.to} onClick={() => setOpen(false)} className="btn-green mt-4" data-testid="mobile-header-cta">{cta.label}</Link>
          </div>
        </nav>
      )}
    </header>
  );
}
